import {useEffect, useRef, useState} from "react";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:8000" : "");

function apiUrl(path) {
    return `${API_BASE_URL}${path}`;
}

export default function WebinarCampaignControl({
    webinarIdentifier,
    webinarTitle,
    report,
    onReportChange,
    onUnauthorized,
}) {
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [feedback, setFeedback] = useState({type: "idle", message: ""});
    const actionButtonRef = useRef(null);
    const confirmButtonRef = useRef(null);
    const confirmationRef = useRef(null);
    const updateInFlightRef = useRef(false);
    const suspensionIncomplete = report.emailsSuspensionIncomplete === true;
    const isSuspended = report.emailsSuspended === true;
    const shouldSuspend = !isSuspended || suspensionIncomplete;

    useEffect(() => {
        if (!isConfirmationOpen) return undefined;

        confirmButtonRef.current?.focus();

        const closeOnEscape = (event) => {
            if (event.key === "Escape" && !isUpdating) {
                setIsConfirmationOpen(false);
                actionButtonRef.current?.focus();
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [isConfirmationOpen, isUpdating]);

    const closeConfirmation = () => {
        if (isUpdating) return;

        setIsConfirmationOpen(false);
        actionButtonRef.current?.focus();
    };

    const keepFocusInsideConfirmation = (event) => {
        if (event.key !== "Tab") return;

        const focusableElements = confirmationRef.current?.querySelectorAll(
            "button:not(:disabled)"
        );

        if (!focusableElements?.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    const updateCampaignStatus = async () => {
        if (updateInFlightRef.current) return;

        const token = localStorage.getItem("webinarAdminToken") || "";

        if (!token) {
            onUnauthorized?.();
            return;
        }

        updateInFlightRef.current = true;
        setIsUpdating(true);
        setFeedback({type: "idle", message: ""});

        try {
            const response = await fetch(
                apiUrl(`/api/admin/webinars/${encodeURIComponent(webinarIdentifier)}/email-campaigns/status`),
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({suspended: shouldSuspend}),
                }
            );
            const data = await response.json().catch(() => ({}));

            if (response.status === 401) {
                onUnauthorized?.();
                return;
            }

            if (data.report) {
                onReportChange(data.report);
            }

            if (!response.ok) {
                throw new Error(data.message || "تعذر تحديث حالة حملات الويبينار.");
            }

            setIsConfirmationOpen(false);
            setFeedback({type: "success", message: data.message});
        } catch (error) {
            setIsConfirmationOpen(false);
            setFeedback({
                type: "error",
                message: error.message || "تعذر تحديث حالة الحملات. حاول مرة أخرى.",
            });
        } finally {
            updateInFlightRef.current = false;
            setIsUpdating(false);
            actionButtonRef.current?.focus();
        }
    };

    const statusClassName = suspensionIncomplete
        ? "is-incomplete"
        : isSuspended
            ? "is-suspended"
            : "is-active";
    const statusTitle = suspensionIncomplete
        ? "لم يتوقف كل شيء بعد"
        : isSuspended
            ? "حملات الويبينار متوقفة"
            : "حملات الويبينار مفعّلة";
    const statusDescription = suspensionIncomplete
        ? "تم منع الرسائل الجديدة من التطبيق، لكن يلزم إعادة محاولة إيقاف بعض حملات Brevo."
        : isSuspended
            ? "لن تُرسل رسائل التأكيد أو التذكيرات أو الرسائل الجماعية حتى تستأنف الحملات."
            : "رسائل التأكيد والتذكيرات والرسائل الجماعية تعمل بصورة طبيعية.";
    const actionLabel = suspensionIncomplete
        ? "إعادة محاولة إيقاف الحملات"
        : isSuspended
            ? "استئناف حملات الويبينار"
            : "إيقاف جميع حملات الويبينار";

    return (
        <section className={`webinar-campaign-control ${statusClassName}`} aria-labelledby="campaign-control-title">
            <div>
                <p className="webinar-campaign-control-eyebrow">حالة الإرسال</p>
                <h2 id="campaign-control-title">{statusTitle}</h2>
                <p>{statusDescription}</p>
                {isSuspended && report.emailsSuspendedBy && (
                    <small>أوقفها المدير: {report.emailsSuspendedBy}</small>
                )}
            </div>
            <button
                ref={actionButtonRef}
                className={shouldSuspend ? "campaign-control-stop" : "campaign-control-resume"}
                type="button"
                onClick={() => {
                    setFeedback({type: "idle", message: ""});
                    setIsConfirmationOpen(true);
                }}
                disabled={isUpdating}
            >
                {actionLabel}
            </button>

            {feedback.message && (
                <p
                    className={`webinar-campaign-feedback webinar-campaign-feedback--${feedback.type}`}
                    role={feedback.type === "error" ? "alert" : "status"}
                    aria-live="polite"
                >
                    {feedback.message}
                </p>
            )}

            {isConfirmationOpen && (
                <div
                    className="webinar-email-confirmation-backdrop"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeConfirmation();
                    }}
                >
                    <section
                        ref={confirmationRef}
                        className="webinar-email-confirmation"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="campaign-confirmation-title"
                        aria-describedby="campaign-confirmation-description"
                        onKeyDown={keepFocusInsideConfirmation}
                    >
                        <p className="webinar-email-confirmation-icon" aria-hidden="true">!</p>
                        <h2 id="campaign-confirmation-title">
                            {shouldSuspend ? "تأكيد إيقاف الحملات" : "تأكيد استئناف الحملات"}
                        </h2>
                        <p id="campaign-confirmation-description">
                            {shouldSuspend
                                ? `سيتم إيقاف رسائل التأكيد والتذكيرات والرسائل الجماعية لويبينار «${webinarTitle || "بدون عنوان"}». ستظل صفحة التسجيل متاحة.`
                                : `سيتم استئناف رسائل ويبينار «${webinarTitle || "بدون عنوان"}». لن تُرسل التذكيرات التي انتهى موعدها أثناء الإيقاف.`}
                        </p>
                        <div className="webinar-email-confirmation-actions">
                            <button type="button" onClick={closeConfirmation} disabled={isUpdating}>
                                إلغاء
                            </button>
                            <button
                                ref={confirmButtonRef}
                                className={shouldSuspend ? "campaign-confirmation-stop" : "webinar-email-confirmation-send"}
                                type="button"
                                onClick={updateCampaignStatus}
                                disabled={isUpdating}
                            >
                                {isUpdating
                                    ? "جاري التنفيذ..."
                                    : shouldSuspend
                                        ? "تأكيد الإيقاف"
                                        : "تأكيد الاستئناف"}
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </section>
    );
}
